// src/pages/financials/PnLTab.tsx
//
// Profit & Loss — classic month-columned P&L from the qbd-pnl recipe.
// Date range inputs (default: trailing 12 months), collapsible sections
// (Income / COGS / Expenses / Other Income / Other Expense), computed rows
// (Gross Profit after COGS, Net Operating Income after Expenses, Net Income
// last), sticky Account column, CSV export of the flattened rows.
//
// Two views: "Original" (raw QuickBooks numbers) and "Modified" (default) —
// the modified view folds in manual adjustments (agent /api/qbd/adjustments)
// so non-operating items like owner draws can be stripped out. The
// Adjustments… button opens a manager modal (list + add + delete).
//
// Month labels are 'yyyy-MM' strings from the agent — rendered as-is.
//
// Columns: "Months" (default) or "Compare" (flag financials.pnlcompare,
// agent 3.20.0+) — the PBI layout: Actual $ / % of income, Prior Year $ /
// %, change vs PY, Budget and variance vs budget, one row per account.

import { useState } from 'react';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileSpreadsheet, RefreshCw, Download, AlertTriangle,
  ChevronDown, ChevronRight, SlidersHorizontal, Trash2,
} from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import type { QbdPnlResponse, QbdPnlSection, QbdPnlLine } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import Modal from '../../components/Modal';
import DateField, { hebrewMonthCaption } from '../../components/DateField';

const fmt0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Whole-dollar currency; negatives red in accounting parentheses. */
function Amt({ v, bold = false }: { v: number; bold?: boolean }) {
  if (v < 0) {
    return <span className={`text-red-400 ${bold ? 'font-semibold' : ''}`}>({fmt0.format(Math.abs(v))})</span>;
  }
  return <span className={bold ? 'font-semibold' : ''}>{fmt0.format(v)}</span>;
}

const pad = (n: number) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const firstOfMonth11Back = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 11);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
};

// CSV: flatten every visible row (section headers, accounts, totals,
// computed lines) into label + per-month amounts + total.
interface FlatRow { label: string; amounts: (number | null)[]; total: number | null }

function exportPnl(data: QbdPnlResponse, sections: QbdPnlSection[], exporter: typeof exportCsv = exportCsv) {
  const rows: FlatRow[] = [];
  const empty = data.months.map(() => null);
  for (const s of sections) {
    rows.push({ label: s.label, amounts: empty, total: null });
    for (const a of s.accounts) {
      rows.push({ label: `  ${a.account}`, amounts: a.amounts, total: a.total });
    }
    rows.push({ label: `Total ${s.label}`, amounts: s.totals, total: s.total });
    if (s.key === 'cogs') rows.push({ label: 'Gross Profit', amounts: data.grossProfit.amounts, total: data.grossProfit.total });
    if (s.key === 'expense') rows.push({ label: 'Net Operating Income', amounts: data.netOperatingIncome.amounts, total: data.netOperatingIncome.total });
  }
  rows.push({ label: 'Net Income', amounts: data.netIncome.amounts, total: data.netIncome.total });

  const cols: CsvColumns<FlatRow> = [
    ['Account', (r) => r.label],
    ...data.months.map((m, i): [string, (r: FlatRow) => string | number] => [m, (r) => r.amounts[i] ?? '']),
    ['Total', (r) => r.total ?? ''],
  ];
  exporter('profit-and-loss', cols, rows);
}

// ── Compare layout ──────────────────────────────────────────────
// One row per line: Actual, % of income, PY, % of income, Δ vs PY,
// Budget, Δ vs Budget. Income totals drive the % columns.

// Only the comparison totals are read, so sections (totals, not amounts) fit too.
type CmpLine = Pick<QbdPnlLine, 'total' | 'py' | 'budget'>;
interface CmpRow { label: string; kind: 'head' | 'account' | 'total' | 'computed' | 'net'; line?: CmpLine; cost?: boolean }

// Higher is worse on cost lines — their deltas colour inverted.
const COST_KEYS = new Set(['cogs', 'expense', 'otherExpense']);

function compareRows(data: QbdPnlResponse, sections: QbdPnlSection[], collapsed: Set<string>): CmpRow[] {
  const rows: CmpRow[] = [];
  for (const s of sections) {
    rows.push({ label: s.label, kind: 'head' });
    if (!collapsed.has(s.key)) {
      for (const a of s.accounts) rows.push({ label: a.account, kind: 'account', line: a, cost: COST_KEYS.has(s.key) });
    }
    rows.push({ label: `Total ${s.label}`, kind: 'total', line: s, cost: COST_KEYS.has(s.key) });
    if (s.key === 'cogs') rows.push({ label: 'Gross Profit', kind: 'computed', line: data.grossProfit });
    if (s.key === 'expense') rows.push({ label: 'Net Operating Income', kind: 'computed', line: data.netOperatingIncome });
  }
  rows.push({ label: 'Net Income', kind: 'net', line: data.netIncome });
  return rows;
}

const pctOf = (v: number, base: number) => (base ? `${((v / base) * 100).toFixed(1)}%` : '—');

function Delta({ v, base, cost = false }: { v: number; base: number; cost?: boolean }) {
  const good = cost ? v < 0 : v > 0;
  const pct = base ? ` (${((v / Math.abs(base)) * 100).toFixed(1)}%)` : '';
  return <span className={v === 0 ? 'text-slate-500' : good ? 'text-emerald-400' : 'text-red-400'}>{v < 0 ? '−' : v > 0 ? '+' : ''}{fmt0.format(Math.abs(v))}{pct}</span>;
}

function exportCompare(data: QbdPnlResponse, sections: QbdPnlSection[], exporter: typeof exportCsv = exportCsv) {
  const incCy = data.sections.find((s) => s.key === 'income')?.total ?? 0;
  const incPy = data.sections.find((s) => s.key === 'income')?.py ?? 0;
  const rows = compareRows(data, sections, new Set());
  const cols: CsvColumns<CmpRow> = [
    ['Account', (r) => (r.kind === 'account' ? `  ${r.label}` : r.label)],
    ['Actual', (r) => r.line?.total ?? ''],
    ['% of Income', (r) => (r.line && incCy ? +((r.line.total / incCy) * 100).toFixed(2) : '')],
    ['Prior Year', (r) => r.line?.py ?? ''],
    ['PY % of Income', (r) => (r.line && incPy ? +(((r.line.py ?? 0) / incPy) * 100).toFixed(2) : '')],
    ['Change vs PY', (r) => (r.line ? +(r.line.total - (r.line.py ?? 0)).toFixed(2) : '')],
    ['Budget', (r) => r.line?.budget ?? ''],
    ['Variance vs Budget', (r) => (r.line ? +(r.line.total - (r.line.budget ?? 0)).toFixed(2) : '')],
  ];
  exporter('profit-and-loss-compare', cols, rows);
}

function CompareTable({ data, sections, collapsed, onToggle }: {
  data: QbdPnlResponse; sections: QbdPnlSection[]; collapsed: Set<string>; onToggle: (k: string) => void;
}) {
  const income = data.sections.find((s) => s.key === 'income');
  const incCy = income?.total ?? 0;
  const incPy = income?.py ?? 0;
  const showBudget = data.hasBudget ?? false;
  const rows = compareRows(data, sections, collapsed);
  const keyOf = (label: string) => sections.find((s) => s.label === label)?.key ?? label;
  const th = 'px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap';
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-slate-700/50 bg-slate-800/50">
          <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[220px]">Account</th>
          <th className={`${th} text-slate-200`}>Actual</th>
          <th className={th}>% Inc</th>
          <th className={th} title={data.pyFrom ? `${data.pyFrom} → ${data.pyTo}` : undefined}>Prior Year</th>
          <th className={th}>% Inc</th>
          <th className={th}>Δ vs PY</th>
          {showBudget && <th className={th}>Budget</th>}
          {showBudget && <th className={th}>Δ vs Budget</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          if (r.kind === 'head') {
            const isCollapsed = collapsed.has(keyOf(r.label));
            return (
              <tr key={`h-${r.label}`} className="border-b border-slate-800/50 bg-slate-800/60">
                <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 font-semibold text-white" colSpan={showBudget ? 8 : 6}>
                  <button onClick={() => onToggle(keyOf(r.label))} className="flex items-center gap-1.5 hover:text-indigo-300 transition-colors">
                    {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                    {r.label}
                  </button>
                </td>
              </tr>
            );
          }
          const l = r.line!;
          const py = l.py ?? 0;
          const bud = l.budget ?? 0;
          const strong = r.kind !== 'account';
          const rowCls = r.kind === 'net' ? 'border-t-2 border-slate-500/60 bg-slate-800/60'
            : r.kind === 'computed' ? 'border-b border-slate-700/50 bg-slate-800/40' : 'border-b border-slate-800/50 hover:bg-slate-700/20';
          const td = `px-3 py-2 text-right tabular-nums whitespace-nowrap ${strong ? 'font-medium text-white' : 'text-slate-300'}`;
          return (
            <tr key={`${r.kind}-${r.label}-${i}`} className={rowCls}>
              <td className={`sticky left-0 z-10 px-3 py-2 whitespace-nowrap ${r.kind === 'account' ? 'bg-slate-900 pl-8 text-slate-300' : r.kind === 'total' ? 'bg-slate-900 pl-8 font-medium text-slate-200' : 'bg-slate-800 font-semibold text-white'}`}>{r.label}</td>
              <td className={td}><Amt v={l.total} bold={strong} /></td>
              <td className={`${td} text-slate-400`}>{pctOf(l.total, incCy)}</td>
              <td className={td}><Amt v={py} bold={strong} /></td>
              <td className={`${td} text-slate-400`}>{pctOf(py, incPy)}</td>
              <td className={td}><Delta v={l.total - py} base={py} cost={r.cost} /></td>
              {showBudget && <td className={td}><Amt v={bud} bold={strong} /></td>}
              {showBudget && <td className={td}><Delta v={l.total - bud} base={bud} cost={r.cost} /></td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function PnLTab() {
  const [dateFrom, setDateFrom] = useState(firstOfMonth11Back);
  const [dateTo, setDateTo] = useState(todayStr);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [version, setVersion] = useState<'original' | 'modified'>('modified');
  const [showAdjustments, setShowAdjustments] = useState(false);
  const { canSee } = useFeatureAccess();
  const compareEnabled = canSee('financials.pnlcompare');
  const [layoutPref, setLayout] = useState<'months' | 'compare'>('months');
  const layout = compareEnabled ? layoutPref : 'months';

  const toggleSection = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const q = useQuery({
    queryKey: ['qbd', 'pnl', dateFrom, dateTo, version],
    queryFn: () => api.getQbdPnl({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, version }),
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  // Skip sections with nothing in them (total 0 and no accounts).
  const sections = (data?.sections ?? []).filter((s) => s.accounts.length > 0 || s.total !== 0);
  const months = data?.months ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <FileSpreadsheet size={20} /> Profit & Loss
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Month-by-month P&L from QuickBooks Desktop — income, COGS, expenses, and net income.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Date Range</span>
          <div className="flex items-center gap-1.5">
            <DateField value={dateFrom} onChange={setDateFrom} className="w-[130px]" />
            <span className="text-slate-600 text-xs">→</span>
            <DateField value={dateTo} onChange={setDateTo} className="w-[130px]" />
          </div>
        </div>
        {/* Original | Modified view toggle */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">View</span>
          <div className="flex items-center gap-1.5">
            {(['original', 'modified'] as const).map((v) => (
              <button key={v} onClick={() => setVersion(v)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all capitalize ${
                  version === v
                    ? 'bg-indigo-500/15 text-indigo-200 border-indigo-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {v}
              </button>
            ))}
          </div>
        </div>
        {compareEnabled && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Columns</span>
            <div className="flex items-center gap-1.5">
              {([['months', 'Months'], ['compare', 'Compare']] as const).map(([v, label]) => (
                <button key={v} onClick={() => setLayout(v)}
                  title={v === 'compare' ? 'Actual vs prior year and budget, with % of income' : 'One column per month'}
                  className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                    layout === v
                      ? 'bg-indigo-500/15 text-indigo-200 border-indigo-500/40'
                      : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {version === 'modified' && (data?.adjustmentsApplied ?? 0) > 0 && (
          <span className="text-[11px] text-slate-500 pb-2">
            ({data!.adjustmentsApplied} adjustment{data!.adjustmentsApplied === 1 ? '' : 's'} applied)
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={() => setShowAdjustments(true)}
          title="Manage the manual adjustments folded into the Modified view"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <SlidersHorizontal size={11} /> Adjustments…
        </button>
        <button
          onClick={() => data && (layout === 'compare' ? exportCompare(data, sections) : exportPnl(data, sections))}
          disabled={!data || sections.length === 0}
          title="Export the P&L to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> Export
        </button>
        <button
          onClick={() => data && (layout === 'compare' ? exportCompare(data, sections, exportXlsx) : exportPnl(data, sections, exportXlsx))}
          disabled={!data || sections.length === 0}
          title="Export to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the P&L</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            {layout === 'compare' ? (
              <CompareTable data={data} sections={sections} collapsed={collapsed} onToggle={toggleSection} />
            ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  {/* Sticky account column — opaque bg so scrolled content doesn't show through */}
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[200px]">Account</th>
                  {months.map((m) => (
                    <th key={m} className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">{m}</th>
                  ))}
                  <th className="px-3 py-2.5 text-right text-slate-300 font-semibold whitespace-nowrap">Total</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => {
                  const isCollapsed = collapsed.has(s.key);
                  const rows: React.ReactNode[] = [];
                  // Section header (chevron toggles the account rows; the
                  // section total row below stays visible either way).
                  rows.push(
                    <tr key={`${s.key}-head`} className="border-b border-slate-800/50 bg-slate-800/60">
                      <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 font-semibold text-white">
                        <button onClick={() => toggleSection(s.key)} className="flex items-center gap-1.5 hover:text-indigo-300 transition-colors">
                          {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                          {s.label}
                        </button>
                      </td>
                      {months.map((m) => <td key={m} className="bg-slate-800/60" />)}
                      <td className="bg-slate-800/60" />
                    </tr>
                  );
                  if (!isCollapsed) {
                    for (const a of s.accounts) {
                      rows.push(
                        <tr key={`${s.key}-${a.account}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                          <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 pl-8 text-slate-300 whitespace-nowrap">{a.account}</td>
                          {a.amounts.map((v, i) => (
                            <td key={months[i] ?? i} className="px-3 py-2 text-right text-slate-300 tabular-nums whitespace-nowrap"><Amt v={v} /></td>
                          ))}
                          <td className="px-3 py-2 text-right text-slate-200 tabular-nums whitespace-nowrap"><Amt v={a.total} /></td>
                        </tr>
                      );
                    }
                  }
                  // Section total — always visible.
                  rows.push(
                    <tr key={`${s.key}-total`} className="border-b border-slate-800/50">
                      <td className="sticky left-0 z-10 bg-slate-900 px-3 py-2 pl-8 font-medium text-slate-200 whitespace-nowrap">Total {s.label}</td>
                      {s.totals.map((v, i) => (
                        <td key={months[i] ?? i} className="px-3 py-2 text-right font-medium text-slate-200 tabular-nums whitespace-nowrap"><Amt v={v} /></td>
                      ))}
                      <td className="px-3 py-2 text-right font-medium text-white tabular-nums whitespace-nowrap"><Amt v={s.total} bold /></td>
                    </tr>
                  );
                  // Computed rows after their sections.
                  if (s.key === 'cogs') {
                    rows.push(
                      <tr key="gross-profit" className="border-b border-slate-700/50 bg-slate-800/40">
                        <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 font-semibold text-white whitespace-nowrap">Gross Profit</td>
                        {data.grossProfit.amounts.map((v, i) => (
                          <td key={months[i] ?? i} className="px-3 py-2 text-right text-white tabular-nums whitespace-nowrap"><Amt v={v} bold /></td>
                        ))}
                        <td className="px-3 py-2 text-right text-white tabular-nums whitespace-nowrap"><Amt v={data.grossProfit.total} bold /></td>
                      </tr>
                    );
                  }
                  if (s.key === 'expense') {
                    rows.push(
                      <tr key="net-operating-income" className="border-b border-slate-700/50 bg-slate-800/40">
                        <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 font-semibold text-white whitespace-nowrap">Net Operating Income</td>
                        {data.netOperatingIncome.amounts.map((v, i) => (
                          <td key={months[i] ?? i} className="px-3 py-2 text-right text-white tabular-nums whitespace-nowrap"><Amt v={v} bold /></td>
                        ))}
                        <td className="px-3 py-2 text-right text-white tabular-nums whitespace-nowrap"><Amt v={data.netOperatingIncome.total} bold /></td>
                      </tr>
                    );
                  }
                  return rows;
                })}
                {/* Net Income — the bottom line */}
                <tr className="border-t-2 border-slate-500/60 bg-slate-800/60">
                  <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 font-bold text-white whitespace-nowrap">Net Income</td>
                  {(data.netIncome.amounts ?? []).map((v, i) => (
                    <td key={months[i] ?? i} className="px-3 py-2.5 text-right text-white tabular-nums whitespace-nowrap"><Amt v={v} bold /></td>
                  ))}
                  <td className="px-3 py-2.5 text-right text-white tabular-nums whitespace-nowrap"><Amt v={data.netIncome.total} bold /></td>
                </tr>
              </tbody>
            </table>
            )}
          </div>
          {sections.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">No P&L activity in the selected range</div>
          )}
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
            {data.dateFrom} → {data.dateTo} · {months.length} month{months.length === 1 ? '' : 's'}
            {layout === 'compare' && data.pyFrom && <span> · prior year {data.pyFrom} → {data.pyTo}</span>}
            {layout === 'compare' && data.pyFrom === undefined && <span className="text-amber-400"> · prior-year / budget columns need agent 3.20.0+</span>}
            {layout === 'compare' && data.hasBudget === false && <span> · no budget for these months</span>}
            {version === 'modified' && (data.adjustmentsApplied ?? 0) > 0 && (
              <span> · {data.adjustmentsApplied} adjustment{data.adjustmentsApplied === 1 ? '' : 's'} applied</span>
            )}
          </div>
        </div>
      )}

      {showAdjustments && <AdjustmentsModal onClose={() => setShowAdjustments(false)} />}
    </div>
  );
}

// ── Adjustments manager ─────────────────────────────────────────
// List + add + delete of the manual adjustments the agent folds into the
// "Modified" P&L. Every mutation invalidates the ['qbd'] query family so
// the P&L (and the Overview summary, if it ever grows a version param)
// refetches with the new adjustment set.

const ACCT_TYPES = ['Income', 'CostOfGoodsSold', 'Expense', 'OtherIncome', 'OtherExpense'];

const currentYm = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmt2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function AdjustmentsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [account, setAccount] = useState('');
  const [acctType, setAcctType] = useState('Expense');
  const [ym, setYm] = useState(currentYm);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');

  const listQ = useQuery({
    queryKey: ['qbd', 'adjustments'],
    queryFn: () => api.getQbdAdjustments(),
    staleTime: 30_000,
    retry: 1,
  });

  const saveM = useMutation({
    mutationFn: () => api.putQbdAdjustment({
      account: account.trim(),
      acctType,
      ym,
      amount: Number(amount),
      memo: memo.trim(),
    }),
    onSuccess: () => {
      setAccount(''); setAmount(''); setMemo('');
      qc.invalidateQueries({ queryKey: ['qbd'] });
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.deleteQbdAdjustment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['qbd'] }),
  });

  const rows = listQ.data ?? [];
  const canAdd = account.trim() !== '' && /^\d{4}-\d{2}$/.test(ym) && amount !== '' && Number.isFinite(Number(amount));
  const ymHebrew = hebrewMonthCaption(ym);

  const inputCls = 'px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50';

  return (
    <Modal title="P&L Adjustments" onClose={onClose} panelClassName="max-w-2xl">
      <div className="space-y-4">
        {listQ.error && (
          <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-xs flex items-center gap-2">
            <AlertTriangle size={14} /> Couldn't load adjustments: {(listQ.error as Error).message}
          </div>
        )}

        {listQ.isLoading ? (
          <div className="flex items-center justify-center h-24"><RefreshCw className="w-5 h-5 text-blue-400 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">
            Adjustments let you strip non-operating items (e.g. owner draws) out of the Modified P&L.
          </p>
        ) : (
          <div className="rounded-lg border border-slate-700/50 overflow-hidden">
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Account</th>
                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Type</th>
                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Month</th>
                    <th className="px-3 py-2 text-right text-slate-400 font-medium">Amount</th>
                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Memo</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                      <td className="px-3 py-2 text-slate-200 whitespace-nowrap">{a.account}</td>
                      <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{a.acctType}</td>
                      <td className="px-3 py-2 text-slate-300 whitespace-nowrap tabular-nums">{a.ym}</td>
                      <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${a.amount < 0 ? 'text-red-400' : 'text-slate-200'}`}>
                        {fmt2.format(a.amount)}
                      </td>
                      <td className="px-3 py-2 text-slate-400 max-w-[180px] truncate" title={a.memo}>{a.memo}</td>
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={() => deleteM.mutate(a.id)}
                          disabled={deleteM.isPending}
                          title="Delete this adjustment"
                          className="p-1 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add form */}
        <div className="rounded-lg border border-slate-700/40 bg-slate-800/20 p-3 space-y-3">
          <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">Add adjustment</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2 flex flex-col gap-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Account</span>
              <input type="text" value={account} onChange={e => setAccount(e.target.value)}
                placeholder="e.g. Owner Draws" className={inputCls} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Type</span>
              <select value={acctType} onChange={e => setAcctType(e.target.value)} className={inputCls}>
                {ACCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Month</span>
              <input type="month" value={ym} onChange={e => setYm(e.target.value)}
                className={`${inputCls} [color-scheme:dark]`} />
              {ymHebrew && <span className="text-[10px] text-slate-500 leading-3">{ymHebrew}</span>}
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Amount</span>
              <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="-1500.00" className={inputCls} />
            </div>
            <div className="col-span-2 sm:col-span-3 flex flex-col gap-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Memo</span>
              <input type="text" value={memo} onChange={e => setMemo(e.target.value)}
                placeholder="Why this adjustment exists" className={inputCls} />
            </div>
          </div>
          {saveM.error && (
            <div className="text-xs text-red-400">Couldn't save: {(saveM.error as Error).message}</div>
          )}
          <div className="flex justify-end">
            <button
              onClick={() => saveM.mutate()}
              disabled={!canAdd || saveM.isPending}
              className="px-3 py-1.5 text-xs rounded-md bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
              {saveM.isPending && <RefreshCw size={12} className="animate-spin" />}
              Add adjustment
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
